/**
 * Typed fetch client for the local REST API (`/api/v1`). Every server call in the web app goes
 * through here so errors have one shape: ApiClientError {status, code, message, details}.
 */
import type { ApiErrorBody } from '@outsource/shared/api';

export const API_BASE = '/api/v1';

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Readonly<Record<string, QueryValue | readonly QueryValue[]>>;

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export function isApiClientError(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError;
}

/** Field-level validation issues from a VALIDATION_ERROR response, keyed by dotted path. */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!isApiClientError(error) || !Array.isArray(error.details)) return {};
  const out: Record<string, string> = {};
  for (const issue of error.details as unknown[]) {
    if (issue && typeof issue === 'object' && 'path' in issue && 'message' in issue) {
      const { path, message } = issue as { path: unknown; message: unknown };
      if (typeof path === 'string' && typeof message === 'string' && !(path in out)) out[path] = message;
    }
  }
  return out;
}

function encodeValue(value: QueryValue): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'boolean') return value ? '1' : '0';
  return String(value);
}

/** `{ period: '2026-09', issuer_id: undefined, include_archived: true }` → `period=2026-09&include_archived=1` */
export function toSearchParams(query?: QueryParams): URLSearchParams {
  const params = new URLSearchParams();
  if (!query) return params;
  for (const [key, raw] of Object.entries(query)) {
    const values = Array.isArray(raw) ? (raw as readonly QueryValue[]) : [raw as QueryValue];
    for (const value of values) {
      const encoded = encodeValue(value);
      if (encoded !== null) params.append(key, encoded);
    }
  }
  return params;
}

export function buildUrl(path: string, query?: QueryParams): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  const search = toSearchParams(query).toString();
  return `${API_BASE}${clean}${search ? `?${search}` : ''}`;
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  if (!value || typeof value !== 'object' || !('error' in value)) return false;
  const error = (value as { error: unknown }).error;
  return !!error && typeof error === 'object' && 'code' in error && 'message' in error;
}

async function readError(response: Response): Promise<ApiClientError> {
  const text = await response.text().catch(() => '');
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  if (isErrorBody(parsed)) {
    const { code, message, details } = parsed.error;
    return new ApiClientError(response.status, code, message, details);
  }
  const message = response.statusText || `Запрос завершился с кодом ${response.status}`;
  return new ApiClientError(response.status, `HTTP_${response.status}`, message);
}

async function send(
  method: string,
  url: string,
  body?: unknown,
  accept = 'application/json',
): Promise<Response> {
  const headers: Record<string, string> = { Accept: accept };
  const init: RequestInit = { method, headers };
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (cause) {
    const reason = cause instanceof Error ? cause.message : 'Сетевой запрос не выполнен';
    throw new ApiClientError(0, 'NETWORK_ERROR', `Не удаётся связаться с локальным сервером: ${reason}`);
  }
  if (!response.ok) {
    const error = await readError(response);
    if (response.status === HTTP_UNAUTHORIZED && !url.startsWith(ME_URL) && !STAY_ON_401.has(error.code)) signInAgain();
    throw error;
  }
  return response;
}

const HTTP_UNAUTHORIZED = 401;
const ME_URL = `${API_BASE}/me`;
/** The code screen says why the session ended (too many wrong codes, W20) before a new login. */
const STAY_ON_401 = new Set(['MFA_LOCKED']);

/**
 * Online, an expired session sends the browser through Keycloak and back to where it was
 * (decisions-log W8). Not while the page shows why the last login failed (`?login=…`), which
 * would loop through Keycloak.
 */
export function signInAgain(): void {
  if (typeof window === 'undefined' || new URLSearchParams(window.location.search).has('login')) return;
  const returnTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
}

async function json<T>(method: string, path: string, body?: unknown, query?: QueryParams): Promise<T> {
  const response = await send(method, buildUrl(path, query), body);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** `attachment; filename="1067-2026.pdf"` → `1067-2026.pdf` (RFC 5987 `filename*` wins). */
export function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through to the plain filename */
    }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || fallback;
}

export interface DownloadResult {
  readonly blob: Blob;
  readonly filename: string;
}

export type UploadFieldValue = string | number | boolean | null | undefined;

/** Multipart body: one or more files under `fileField` (default `file`) plus plain text fields. */
export interface UploadInput {
  readonly files: Blob | readonly Blob[];
  /** Multipart field name of the files; default `file` (repeat it for several files). */
  readonly fileField?: string;
  /** Extra form fields; null/undefined are skipped, booleans become `1` / `0`. */
  readonly fields?: Readonly<Record<string, UploadFieldValue>>;
}

const DEFAULT_FILE_FIELD = 'file';

/** Builds the multipart FormData for `api.upload` (files keep their names when they are `File`s). */
export function toFormData({ files, fileField = DEFAULT_FILE_FIELD, fields }: UploadInput): FormData {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields ?? {})) {
    const encoded = encodeValue(value);
    if (encoded !== null) form.append(key, encoded);
  }
  const list: readonly Blob[] = files instanceof Blob ? [files] : files;
  for (const file of list) form.append(fileField, file);
  return form;
}

export const api = {
  get: <T>(path: string, query?: QueryParams) => json<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown, query?: QueryParams) => json<T>('POST', path, body, query),
  patch: <T>(path: string, body?: unknown) => json<T>('PATCH', path, body),
  put: <T>(path: string, body?: unknown) => json<T>('PUT', path, body),
  del: <T>(path: string, body?: unknown) => json<T>('DELETE', path, body),

  /**
   * POSTs multipart/form-data (documents, client invoice PDFs, imports). The browser sets the
   * boundary header; errors use the same ApiClientError shape as every other call.
   */
  upload: <T>(path: string, input: UploadInput | FormData) =>
    json<T>('POST', path, input instanceof FormData ? input : toFormData(input)),

  /** Fetches a binary (PDF, zip, CSV, XLSX): GET without a body, POST with one. */
  async download(path: string, body?: unknown, query?: QueryParams): Promise<DownloadResult> {
    const method = body === undefined ? 'GET' : 'POST';
    const response = await send(method, buildUrl(path, query), body, '*/*');
    const fallback = path.split('/').filter(Boolean).pop() ?? 'download';
    return {
      blob: await response.blob(),
      filename: filenameFromDisposition(response.headers.get('Content-Disposition'), fallback),
    };
  },
};

/** Hands a downloaded blob to the browser as a file save. */
export function saveBlob({ blob, filename }: DownloadResult): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
